import type { CSSProperties, Dispatch, ReactNode } from "react"

import type { SegmentAnalysisAction, SegmentAnalysisState } from "../hooks/use-segment-analysis"
import type {
  AddressableLine,
  Segment,
  SegmentAnalysisRole,
  SegmentAnalysisView,
  SegmentChartKind,
  SegmentKpiDensity,
  SegmentPortfolioSummary,
} from "../types"
import type { LucideIcon } from "@/components/icons"

import { Building2, Download, Globe2, LayoutGrid, Plus, Share2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import { cn } from "@/lib/utils"

import { CountUp } from "./count-up"
import { SegmentAnalysisCharts } from "./segment-analysis-charts"
import { SegmentAnalysisFilters } from "./segment-analysis-filters"
import { SegmentCard } from "./segment-card"
import { buildKpiCatalog, defaultKpiCardIds, selectKpiCards } from "../utils/kpi-cards"

type SegmentAnalysisPortfolioProps = {
  addressableLines: AddressableLine[]
  allSegments: Segment[]
  canCreate: boolean
  /** Hero-card stat ids (#2069 B2); absent → classic trio. */
  cardStats?: string[]
  /** Named filter presets (#2069 C1) + handlers, rendered in the filter bar. */
  filterPresets?: string[]
  onApplyFilterPreset?: (name: string) => void
  onDeleteFilterPreset?: (name: string) => void
  onSaveFilterPreset?: (name: string) => void
  /** Stale-segment threshold for the KPI card (#2069 A2). */
  staleDays?: number
  /** The user-selected portfolio graphs, stacked in order (§G9, #2068 B1). */
  charts: SegmentChartKind[]
  currentUserOid: string | null
  /** The per-user "Customize view" control (§G9), rendered in the header button row. */
  customizeSlot?: ReactNode
  dispatch: Dispatch<SegmentAnalysisAction>
  headerSlot?: ReactNode
  /** User-selected KPI card ids in display order (#2067/#2068); absent → per-view defaults. */
  kpiCardIds?: string[]
  /** KPI row density (#2068 A3); absent → comfortable. */
  kpiDensity?: SegmentKpiDensity
  /**
   * Market/Owner-only scope backing the Companies/Map KPI row (#3306 #7 review
   * finding 2) — same set those views render, so the KPIs above them can't
   * carry a hidden Portfolio-only search/Favourites narrowing.
   */
  marketOwnerFilteredSegments: Segment[]
  marketOwnerSummary: SegmentPortfolioSummary
  owners: string[]
  pendingCounts: Record<string, number>
  role: SegmentAnalysisRole
  /**
   * The *resolved* selection id (`resolveSelectedSegment`), not the raw
   * `state.selectedSegmentId`: after a filter hides the selected segment the
   * detail pane falls back to another one, and the charts/cards highlight that.
   */
  selectedSegmentId: string | null
  showFavourites: boolean
  state: SegmentAnalysisState
  summary: SegmentPortfolioSummary
  view: SegmentAnalysisView
  /** Rendered in place of the cards/charts when view is "companies" or "map". */
  viewSlot?: ReactNode
  visibleSegments: Segment[]
  onExport: () => void
  onToggleFavourite: (segmentId: string) => void
  onViewChange: (view: SegmentAnalysisView) => void
}

const VIEW_OPTIONS: Array<{ id: SegmentAnalysisView; label: string; icon: LucideIcon }> = [
  { id: "portfolio", label: "Portfolio", icon: LayoutGrid },
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "map", label: "Map", icon: Share2 },
]

export function SegmentAnalysisPortfolio({
  addressableLines,
  allSegments,
  canCreate,
  cardStats,
  charts,
  filterPresets,
  onApplyFilterPreset,
  onDeleteFilterPreset,
  onSaveFilterPreset,
  staleDays,
  currentUserOid,
  customizeSlot,
  dispatch,
  headerSlot,
  kpiCardIds,
  kpiDensity,
  marketOwnerFilteredSegments,
  marketOwnerSummary,
  owners,
  pendingCounts,
  role,
  selectedSegmentId,
  showFavourites,
  state,
  summary,
  view,
  viewSlot,
  visibleSegments,
  onExport,
  onToggleFavourite,
  onViewChange,
}: SegmentAnalysisPortfolioProps) {
  const title = role === "owner" ? "Your segments" : "Segment portfolio"
  // Scoped set for both the header count and the KPI row (#3311 review finding
  // 1): Portfolio sums `visibleSegments` (search + Favourites included) so it
  // reacts to its own filter bar; Companies/Map have no control for those two,
  // so they sum `marketOwnerFilteredSegments` instead — the same set rendered
  // below/beside them (#3306 #7 review finding 2). The subtitle must track the
  // same scope, or a sticky Portfolio-only search/Favourites filter leaks into
  // the Companies/Map header count while the KPI row and map/grid below it
  // already show the full Market/Owner-scoped set.
  const scopedSegments = view === "portfolio" ? visibleSegments : marketOwnerFilteredSegments
  const subtitle =
    role === "owner"
      ? `${scopedSegments.length} segment${scopedSegments.length === 1 ? "" : "s"} shown`
      : `${scopedSegments.length} of ${allSegments.length} segments across market groups`
  // KPI row (#2067): full honest catalog, narrowed to the user's selection (or
  // the classic defaults).
  const kpiSegments = scopedSegments
  const kpiSummary = view === "portfolio" ? summary : marketOwnerSummary
  const catalog = buildKpiCatalog({
    summary: kpiSummary,
    segments: kpiSegments,
    addressableLines,
    pendingCounts,
    staleDays,
  })
  const kpis = selectKpiCards(
    catalog,
    kpiCardIds,
    defaultKpiCardIds(kpiSummary, addressableLines, view)
  )
  const compact = kpiDensity === "compact"
  // A tenant with no segments at all (fresh live-data install) gets a real
  // first-run empty state instead of zeroed KPIs, blank charts, and the
  // "…match the current filters" message that implies data exists (#2768).
  const isPortfolioEmpty = allSegments.length === 0
  // The full Market/Owner/search/Favourites filter bar is portfolio-only —
  // Companies and Map carry their own search/legend, so a second search bar
  // there would compete (§G9). But Market + Owner already narrow
  // model.visibleSegments for those views too (#3306 #7); expose just those
  // two chip groups there instead of hiding filter state entirely. The Map's
  // in-canvas legend filter (role/market toggles on the graph itself) is
  // unchanged.
  const filtersSlot =
    view === "portfolio" ? (
      <SegmentAnalysisFilters
        allSegments={allSegments}
        dispatch={dispatch}
        owners={owners}
        presets={filterPresets}
        showFavourites={showFavourites}
        state={state}
        onApplyPreset={onApplyFilterPreset}
        onDeletePreset={onDeleteFilterPreset}
        onSavePreset={onSaveFilterPreset}
      />
    ) : (
      <SegmentAnalysisFilters
        marketAndOwnerOnly
        allSegments={allSegments}
        dispatch={dispatch}
        owners={owners}
        showFavourites={false}
        state={state}
      />
    )

  return (
    <div className="mx-auto grid max-w-[1480px] gap-5 px-4 py-5 sm:px-6 lg:py-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-foreground text-2xl font-semibold tracking-normal">{title}</h2>
          <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
          <ViewSwitch value={view} onChange={onViewChange} />
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          {headerSlot}
          <div className="flex flex-wrap gap-2">
            <Button className="h-10 shadow-sm" variant="outline" onClick={onExport}>
              <Download className="size-4" />
              Export
            </Button>
            {customizeSlot}
            {canCreate ? (
              <Button
                className="h-10 font-semibold shadow-sm"
                onClick={() => dispatch({ type: "openWizard" })}
              >
                <Plus className="size-4" />
                New segment
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      {/* filtersSlot: full bar on Portfolio, Market+Owner chips only elsewhere (#3306 #7). */}
      {!isPortfolioEmpty && filtersSlot}

      {isPortfolioEmpty ? null : (
        <section
          className={cn(
            "grid",
            compact
              ? "gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
              : "gap-4 md:grid-cols-2 xl:grid-cols-4"
          )}
        >
          {kpis.map((kpi, index) => (
            <KpiCard
              key={kpi.id}
              compact={compact}
              icon={kpi.icon}
              index={index}
              label={kpi.label}
              subLabel={kpi.subLabel}
              value={kpi.value}
            />
          ))}
        </section>
      )}

      {view === "portfolio" && isPortfolioEmpty ? (
        <section className="border-border bg-card rounded-lg border border-dashed px-6 shadow-sm sm:px-12">
          <EmptyState
            className="mx-auto max-w-lg"
            icon={Globe2}
            title="No segments yet"
            description={
              canCreate
                ? "Create your first segment to start mapping its market, customers and competitors — the AI agents keep it current from there."
                : "Segments appear here once your sales organisation creates them."
            }
            primaryAction={
              canCreate ? (
                <Button onClick={() => dispatch({ type: "openWizard" })}>
                  <Plus className="size-4" />
                  New segment
                </Button>
              ) : undefined
            }
          />
        </section>
      ) : null}

      {view !== "portfolio" ? (
        <div key={view} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
          {viewSlot}
        </div>
      ) : (
        !isPortfolioEmpty && (
          <div className="animate-in fade-in-0 grid gap-5 duration-200">
            <section aria-label="Segment charts">
              <div className="grid gap-5">
                {charts.map((kind) => (
                  <SegmentAnalysisCharts
                    key={kind}
                    chart={kind}
                    dispatch={dispatch}
                    segments={visibleSegments}
                    selectedSegmentId={selectedSegmentId}
                    showTotal={state.showTotal}
                  />
                ))}
              </div>
            </section>

            {visibleSegments.length === 0 ? (
              <section className="border-border bg-card text-muted-foreground rounded-lg border border-dashed p-10 text-center text-sm">
                No segments match the current filters.
              </section>
            ) : (
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {visibleSegments.map((segment, index) => (
                  <SegmentCard
                    key={segment.id}
                    addressableLines={addressableLines}
                    cardStats={cardStats}
                    dispatch={dispatch}
                    index={index}
                    isActive={selectedSegmentId === segment.id}
                    isOwn={Boolean(currentUserOid && segment.ownerOid === currentUserOid)}
                    pendingCount={pendingCounts[segment.id] ?? 0}
                    segment={segment}
                    isFavourite={Boolean(
                      currentUserOid && segment.favouriteBy?.includes(currentUserOid)
                    )}
                    onToggleFavourite={
                      showFavourites ? () => onToggleFavourite(segment.id) : undefined
                    }
                  />
                ))}
              </section>
            )}
          </div>
        )
      )}
    </div>
  )
}

function KpiCard({
  compact = false,
  icon: Icon,
  index = 0,
  label,
  subLabel,
  value,
}: {
  /** Dense variant (#2068 A3): tighter paddings, smaller value, sublabel dropped. */
  compact?: boolean
  icon: LucideIcon
  index?: number
  label: string
  subLabel: string
  value: string
}) {
  return (
    <Card
      className="card-interactive segment-card-enter rounded-xl shadow-sm"
      style={{ "--segment-index": index } as CSSProperties}
    >
      <CardContent
        className={cn("relative flex items-center", compact ? "gap-2.5 p-3" : "gap-4 p-4")}
      >
        <div
          className={cn(
            "bg-muted text-primary flex shrink-0 items-center justify-center rounded-xl",
            compact ? "size-8" : "size-11"
          )}
        >
          <Icon className={compact ? "size-4" : "size-5"} />
        </div>
        <div className="min-w-0" title={compact ? subLabel : undefined}>
          <p className="text-muted-foreground truncate text-xs font-semibold tracking-[0.12em] uppercase">
            {label}
          </p>
          <p
            className={cn(
              "text-foreground mt-1 leading-tight font-semibold tracking-normal",
              compact ? "text-xl" : "text-3xl"
            )}
          >
            <CountUp value={value} />
          </p>
          {compact ? null : <p className="text-muted-foreground mt-1 text-xs">{subLabel}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function ViewSwitch({
  value,
  onChange,
}: {
  value: SegmentAnalysisView
  onChange: (view: SegmentAnalysisView) => void
}) {
  return (
    <div className="bg-muted text-muted-foreground mt-3 inline-flex rounded-md p-0.5 text-xs font-medium shadow-inner">
      {VIEW_OPTIONS.map((option) => {
        const Icon = option.icon
        return (
          <button
            key={option.id}
            aria-pressed={value === option.id}
            type="button"
            className={cn(
              "btn-spring inline-flex h-8 items-center gap-1.5 rounded px-3 transition",
              value === option.id
                ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                : "hover:text-foreground"
            )}
            onClick={() => onChange(option.id)}
          >
            <Icon className="size-3.5" />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
